import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { Button, Select, Table, TextInput, Textarea } from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  createSite,
  deleteSite,
  fetchDevices,
  fetchSites,
  updateDevice,
  updateSite,
} from '../../admin/server-fns'
import type { SerializedSite, SiteInput } from '../../admin/server-fns'
import { BulkAssignPanel } from '../../admin/aggregate'
import {
  AdminRouteError,
  Modal,
  PageHeader,
  Pagination,
  Panel,
  Spinner,
  formatDate,
  tdCls,
  thCls,
  totalPages,
} from '../../admin/ui'

const PAGE_LIMIT = 20

export const Route = createFileRoute('/admin/sites')({
  loader: async () => fetchSites({ data: { page: 1, limit: PAGE_LIMIT } }),
  component: Sites,
  errorComponent: AdminRouteError,
})

function Sites() {
  const initialData = Route.useLoaderData()

  const [sites, setSites] = useState<SerializedSite[]>(initialData.items)
  const [total, setTotal] = useState(initialData.total)
  const [pages, setPages] = useState(totalPages(initialData.total, initialData.limit))
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchSites({
        data: {
          page,
          limit: PAGE_LIMIT,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      })
      setSites(data.items)
      setTotal(data.total)
      setPages(totalPages(data.total, data.limit))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load sites.')
    } finally {
      setLoading(false)
    }
  }, [page, debouncedSearch])

  useEffect(() => {
    if (page !== 1 || debouncedSearch) void refresh()
  }, [refresh, page, debouncedSearch])

  const [addOpen, setAddOpen] = useState(false)
  const [editSite, setEditSite] = useState<SerializedSite | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<SerializedSite | null>(null)
  const [assignSite, setAssignSite] = useState<SerializedSite | null>(null)

  return (
    <>
      <PageHeader
        kicker="Fleet"
        title="Sites"
        actions={<Button onClick={() => setAddOpen(true)}>Add site</Button>}
      />

      <div className="mb-2">
        <TextInput
          type="search"
          placeholder="Search name, code…"
          className="w-full max-w-xs"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value)
            setPage(1)
          }}
        />
      </div>

      <p className="mb-3 mt-0 font-mono text-xs text-slate-400 dark:text-slate-500">
        {total} site{total === 1 ? '' : 's'}
      </p>

      {error ? (
        <Panel className="mb-4 border-red-300 px-4 py-3 text-sm text-red-700 dark:border-red-500/40 dark:text-red-400">
          {error}{' '}
          <Button variant="subtle" size="compact-sm" onClick={() => void refresh()}>
            Retry
          </Button>
        </Panel>
      ) : null}

      <div className="space-y-6">
        <Panel>
          {loading ? (
            <Spinner />
          ) : sites.length === 0 ? (
            <p className="m-0 px-4 py-12 text-center text-sm text-slate-500 dark:text-slate-400">
              No sites match. Clear the search, or add a site.
            </p>
          ) : (
            <Table.ScrollContainer minWidth={860}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th className={thCls}>Name</Table.Th>
                    <Table.Th className={thCls}>Code</Table.Th>
                    <Table.Th className={thCls}>Timezone</Table.Th>
                    <Table.Th className={thCls}>Contact</Table.Th>
                    <Table.Th className={thCls}>Created</Table.Th>
                    <Table.Th className={thCls} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sites.map((s) => (
                    <Table.Tr key={s.id}>
                      <Table.Td className={tdCls}>{s.name}</Table.Td>
                      <Table.Td className={`${tdCls} font-mono text-xs`}>
                        {s.code ?? '—'}
                      </Table.Td>
                      <Table.Td className={`${tdCls} font-mono text-xs`}>
                        {s.timezone ?? '—'}
                      </Table.Td>
                      <Table.Td className={`${tdCls} font-mono text-xs`}>
                        {s.contactEmail ?? '—'}
                      </Table.Td>
                      <Table.Td className={`${tdCls} font-mono text-xs`}>
                        {formatDate(s.createdAt)}
                      </Table.Td>
                      <Table.Td className={`${tdCls} text-right`}>
                        <span className="flex justify-end gap-2 whitespace-nowrap">
                          <Link
                            to="/admin/devices"
                            search={{ siteId: s.id }}
                            className="text-xs font-semibold text-blue-700 no-underline hover:underline dark:text-blue-400"
                          >
                            Devices →
                          </Link>
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={() => setAssignSite(s)}
                          >
                            Assign device
                          </Button>
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            onClick={() => setEditSite(s)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            color="red"
                            onClick={() => setDeleteTarget(s)}
                          >
                            Delete
                          </Button>
                        </span>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Panel>

        <Pagination page={page} pages={pages} onChange={setPage} />

        <BulkAssignPanel target="siteId" onDone={() => void refresh()} />
      </div>

      <SiteFormModal
        open={addOpen}
        site={null}
        onClose={() => setAddOpen(false)}
        onSaved={() => {
          setAddOpen(false)
          void refresh()
        }}
      />
      <SiteFormModal
        open={editSite !== null}
        site={editSite}
        onClose={() => setEditSite(null)}
        onSaved={() => {
          setEditSite(null)
          void refresh()
        }}
      />
      <DeleteSiteModal
        site={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => {
          setDeleteTarget(null)
          void refresh()
        }}
      />
      <AssignDeviceModal site={assignSite} onClose={() => setAssignSite(null)} />
    </>
  )
}

function SiteFormModal({
  open,
  site,
  onClose,
  onSaved,
}: {
  open: boolean
  site: SerializedSite | null
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [timezone, setTimezone] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [address, setAddress] = useState('{}')
  const [operatingHours, setOperatingHours] = useState('{}')
  const [configOverrides, setConfigOverrides] = useState('{}')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(site?.name ?? '')
      setCode(site?.code ?? '')
      setTimezone(site?.timezone ?? '')
      setContactEmail(site?.contactEmail ?? '')
      setAddress(site?.address ?? '{}')
      setOperatingHours(site?.operatingHours ?? '{}')
      setConfigOverrides(site?.configOverrides ?? '{}')
      setError(null)
    }
  }, [open, site])

  const handleSave = async () => {
    const jsonFields: Array<[string, string]> = [
      ['Address', address],
      ['Operating hours', operatingHours],
      ['Config overrides', configOverrides],
    ]
    for (const [label, value] of jsonFields) {
      try {
        JSON.parse(value)
      } catch {
        setError(`${label} is not valid JSON.`)
        return
      }
    }
    setBusy(true)
    setError(null)
    const body: SiteInput = {
      name,
      code: code || null,
      timezone: timezone || null,
      contactEmail: contactEmail || null,
      address,
      operatingHours,
      configOverrides,
    }
    try {
      if (site) await updateSite({ data: { siteId: site.id, body } })
      else await createSite({ data: body })
      onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the site.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={site ? 'Edit site' : 'Add site'}>
      <TextInput
        label="Name"
        required
        data-autofocus
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />
      <div className="grid grid-cols-2 gap-3">
        <TextInput
          label="Code (optional)"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />
        <TextInput
          label="Timezone (optional)"
          placeholder="Europe/Prague"
          value={timezone}
          onChange={(e) => setTimezone(e.currentTarget.value)}
        />
      </div>
      <TextInput
        label="Contact email (optional)"
        type="email"
        value={contactEmail}
        onChange={(e) => setContactEmail(e.currentTarget.value)}
      />
      <Textarea
        label="Address (JSON)"
        rows={3}
        classNames={{ input: 'font-mono text-xs' }}
        value={address}
        onChange={(e) => setAddress(e.currentTarget.value)}
      />
      <Textarea
        label="Operating hours (JSON)"
        rows={3}
        classNames={{ input: 'font-mono text-xs' }}
        value={operatingHours}
        onChange={(e) => setOperatingHours(e.currentTarget.value)}
      />
      <Textarea
        label="Config overrides (JSON)"
        rows={3}
        classNames={{ input: 'font-mono text-xs' }}
        value={configOverrides}
        onChange={(e) => setConfigOverrides(e.currentTarget.value)}
      />
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <Button disabled={!name.trim()} loading={busy} onClick={() => void handleSave()}>
        {site ? 'Save changes' : 'Create site'}
      </Button>
    </Modal>
  )
}

function DeleteSiteModal({
  site,
  onClose,
  onDeleted,
}: {
  site: SerializedSite | null
  onClose: () => void
  onDeleted: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleDelete = async () => {
    if (!site) return
    setBusy(true)
    setError(null)
    try {
      await deleteSite({ data: { siteId: site.id } })
      onDeleted()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the site.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={site !== null} onClose={onClose} title="Delete site">
      <p className="m-0 text-sm text-slate-600 dark:text-slate-400">
        Deleting <span className="font-semibold">{site?.name}</span> detaches its devices —
        they keep running but become unassigned. This cannot be undone.
      </p>
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="flex gap-2">
        <Button color="red" variant="outline" loading={busy} onClick={() => void handleDelete()}>
          Delete site
        </Button>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  )
}

function AssignDeviceModal({
  site,
  onClose,
}: {
  site: SerializedSite | null
  onClose: () => void
}) {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>([])
  const [deviceId, setDeviceId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [assigned, setAssigned] = useState<string | null>(null)

  useEffect(() => {
    if (!site) return
    fetchDevices({
      data: { page: 1, limit: 10, ...(debouncedSearch ? { search: debouncedSearch } : {}) },
    })
      .then((data) =>
        setOptions(
          data.items.map((d) => ({
            value: d.id,
            label: `${d.serialNumber} · ${d.name}${d.siteId ? ` (site ${d.siteId})` : ''}`,
          })),
        ),
      )
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Could not load devices.'),
      )
  }, [site, debouncedSearch])

  const close = () => {
    setSearch('')
    setDeviceId(null)
    setError(null)
    setAssigned(null)
    onClose()
  }

  const handleAssign = async () => {
    if (!site || !deviceId) return
    setBusy(true)
    setError(null)
    try {
      const updated = await updateDevice({
        data: { deviceId, body: { siteId: site.id } },
      })
      setAssigned(`${updated.serialNumber} assigned to ${site.name}.`)
      setDeviceId(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not assign the device.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={site !== null} onClose={close} title={`Assign device to ${site?.name ?? ''}`}>
      <Select
        label="Device"
        placeholder="Search serial, name…"
        data={options}
        value={deviceId}
        onChange={setDeviceId}
        searchable
        searchValue={search}
        onSearchChange={setSearch}
        filter={({ options: opts }) => opts}
        nothingFoundMessage="No devices match."
      />
      {assigned ? (
        <p className="m-0 font-mono text-xs text-emerald-600 dark:text-emerald-400">{assigned}</p>
      ) : null}
      {error ? <p className="m-0 text-sm text-red-600 dark:text-red-400">{error}</p> : null}
      <div className="flex gap-2">
        <Button disabled={!deviceId} loading={busy} onClick={() => void handleAssign()}>
          Assign device
        </Button>
        <Button variant="default" onClick={close}>
          {assigned ? 'Done' : 'Cancel'}
        </Button>
      </div>
    </Modal>
  )
}
